import {
    type ColorSchemeDefinition,
    Icon,
    InitialsAvatar,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
    useSidebar,
} from "@elit-hair/elit-ui";
import { type ReactNode, useState } from "react";
import type { IconType } from "react-icons";
import { TbLogout, TbMoon, TbPalette, TbSun } from "react-icons/tb";

import { Link } from "../link";

export type NavigationItem = {
    /** Path or URL for the navigation item */
    path: string;
    /** Display name for the navigation item */
    name: string;
    /** Optional icon component (from react-icons) */
    icon?: IconType;
};

type SidebarUserButtonProps = {
    /** User name to display */
    userName?: string;
    /** Custom greeting component or text to display */
    greeting?: ReactNode;
    /** Whether the logout button should be disabled */
    isLogoutDisabled?: boolean;
    /** Current theme value */
    theme?: "light" | "dark" | "system";
    /** Current color scheme value */
    colorScheme?: ColorSchemeDefinition;
    /** Available color scheme options */
    colorSchemeOptions?: ColorSchemeDefinition[];
    /** Callback when theme toggle button is clicked */
    onToggleTheme?: () => void;
    /** Callback when color scheme changes */
    onColorSchemeChange?: (scheme: ColorSchemeDefinition) => void;
    /** Callback when logout button is clicked */
    onLogout?: () => void;
    /** Array of navigation items to display (e.g., profile, settings) */
    navigationItems?: NavigationItem[];
    /** Custom logout confirmation dialog */
    logoutDialog?: ReactNode;
    /** Avatar size override */
    avatarSize?: "sm" | "md" | "lg";
    /** Custom className for the popover content */
    popoverClassName?: string;
};

export function SidebarUserButton({
    userName = "User",
    greeting,
    isLogoutDisabled = false,
    theme = "light",
    colorScheme,
    colorSchemeOptions = [],
    onToggleTheme,
    onColorSchemeChange,
    onLogout,
    navigationItems = [],

    logoutDialog,
    avatarSize,
    popoverClassName,
}: SidebarUserButtonProps) {
    const { state } = useSidebar();
    const [showColorSchemePopover, setShowColorSchemePopover] = useState(false);

    const isCollapsed = state === "collapsed";
    const avatarSizeToUse = avatarSize || (isCollapsed ? "sm" : "md");

    return (
        <>
            <SidebarMenuItem>
                <Popover>
                    <PopoverTrigger asChild>
                        <SidebarMenuButton size="lg">
                            <InitialsAvatar name={userName} size={avatarSizeToUse} />
                            {!isCollapsed && (
                                <div className="flex flex-col items-start text-left">
                                    {greeting && (
                                        <div className="text-xs leading-none text-muted-foreground">{greeting}</div>
                                    )}
                                    <span className="text-sm font-medium leading-none mt-1">{userName}</span>
                                </div>
                            )}
                        </SidebarMenuButton>
                    </PopoverTrigger>
                    <PopoverContent
                        className={`w-56 ml-2 p-0 overflow-hidden ${popoverClassName || ""}`}
                        align="center"
                        side="right"
                        sideOffset={-10}
                    >
                        <div className="flex flex-col">
                            <p className="text-sm text-muted-foreground my-2 px-4 opacity-50">{userName}</p>
                            {navigationItems.map((item) => {
                                const IconComponent = item.icon;

                                return (
                                    <div
                                        key={item.path}
                                        className="flex items-center gap-2 px-4 py-2.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
                                    >
                                        {IconComponent && <IconComponent className="text-lg shrink-0" size={20} />}
                                        {/* {LinkComponent ? (
                                            <LinkComponent to={item.path} className="flex-1">
                                                {item.name}
                                            </LinkComponent>
                                        ) : (
                                            <a href={item.path} className="flex-1">
                                                {item.name}
                                            </a>
                                        )} */}
                                        <Link to={item.path} className="flex-1">
                                            {item.name}
                                        </Link>
                                    </div>
                                );
                            })}
                            <SidebarSeparator />
                            <div className="flex">
                                {onToggleTheme && (
                                    <button
                                        onClick={onToggleTheme}
                                        className="w-full flex-1 justify-center text-muted-foreground hover:bg-muted hover:text-foreground rounded-none flex items-center gap-2 px-4 py-2.5"
                                    >
                                        {theme === "dark" ? <Icon icon={TbSun} /> : <Icon icon={TbMoon} />}
                                    </button>
                                )}
                                {colorSchemeOptions.length > 0 && onColorSchemeChange && (
                                    <Popover open={showColorSchemePopover} onOpenChange={setShowColorSchemePopover}>
                                        <PopoverTrigger asChild>
                                            <button className="w-full flex-1 justify-center text-muted-foreground hover:bg-muted hover:text-foreground rounded-none flex items-center gap-2 px-4 py-2.5">
                                                <Icon icon={TbPalette} />
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-56 p-1" align="end" side="top" sideOffset={5}>
                                            <div className="flex flex-col">
                                                {colorSchemeOptions.map((scheme) => (
                                                    <button
                                                        key={scheme.value}
                                                        onClick={() => {
                                                            onColorSchemeChange(scheme);
                                                            setShowColorSchemePopover(false);
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                                                            colorScheme?.value === scheme.value
                                                                ? "bg-primary text-primary-foreground"
                                                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                                        }`}
                                                    >
                                                        {scheme.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                )}
                                {onLogout && (
                                    <button
                                        onClick={onLogout}
                                        disabled={isLogoutDisabled}
                                        className="w-full flex-1 justify-center text-red-500 hover:bg-red-500/20 hover:text-red-600 rounded-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 px-4 py-2.5"
                                    >
                                        <Icon icon={TbLogout} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            </SidebarMenuItem>
            {logoutDialog}
        </>
    );
}
